import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { concatMap, switchMap, map, tap, take, filter } from 'rxjs/operators';
import { IrisThemeActionsTypes, InitThemeComplete } from '@iris/common/redux/actions/theme-actions';
import { IrisLocalStorage } from '@iris/common/modules/frontend-storage';
import * as fromGlobal from '../reducers/global.reducer';
import { getTheme as getThemeSelector } from '../reducers/index';
import { Store, select } from '@ngrx/store';
import { IrisTheme, IrisThemeI } from '@iris/common/models/IrisTheme';
import { IrisUserService } from '@iris/common/services/user.service';

export const THEME_STORAGE_KEY = 'iris_user_theme';

@Injectable()
export class IrisThemeEffects {
  private readonly store = inject<Store<fromGlobal.State>>(Store);
  private readonly localStorage = inject(IrisLocalStorage);
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(IrisUserService);

  private toggleStrabagControlsAttribute(enabled: boolean): void {
    document.body.setAttribute('data-controls-view', enabled ? 'strabag' : 'iris');
  }

  initTheme$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        switchMap(() => this.userService.me$.pipe(
          filter(user => !!user),
          take(1),
        )),
        map(() => {
          const savedTheme = this.localStorage.getItem<IrisThemeI>(THEME_STORAGE_KEY);
          const theme = savedTheme ? new IrisTheme(savedTheme) : new IrisTheme();

          this.toggleStrabagControlsAttribute(theme?.strabagControlsEnabled);

          return new InitThemeComplete(theme);
        }),
      ),
  );

  saveTheme$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          IrisThemeActionsTypes.SetTheme,
          IrisThemeActionsTypes.UpdateDarkMode,
          IrisThemeActionsTypes.UpdateStrabagControls,
          IrisThemeActionsTypes.UpdateStrabagColors,
        ),
        // We need to get the latest theme from the store after the theme has been updated,
        // so we use concatMap to wait for the action to be processed before selecting the theme from the store
        concatMap(() => this.store.pipe(select(getThemeSelector), take(1))),
        tap((theme) => {
          this.localStorage.setItem<IrisThemeI>(THEME_STORAGE_KEY, theme);
          this.toggleStrabagControlsAttribute(theme?.strabagControlsEnabled);
        }),
      ),
    { dispatch: false }, // important to prevent dispatching an action after this effect, since it's only for side effects (saving to localStorage)
  );
}
